import { type ReactElement, useState, useEffect } from 'react';
import { NeonButtonFooter, NeonModalDialog, NeonRadioField } from '@ps-refarch-ux/neon';
import { NeonButtonTypes } from '@/constants/neon-constants';
import {
  COURSE_STATUS_OPTIONS,
  BULK_STATUS_MODAL_TEXT,
} from '@/constants/course-catalog-constants';
import { CourseStatus } from '@/types';
import modalStyles from './course-catalog-modals.module.scss';

export interface BulkChangeStatusModalProps {
  isOpen: boolean;
  selectedCount: number;
  onSave: (status: string) => void;
  onClose: () => void;
  isSaving: boolean;
}

export function BulkChangeStatusModal({
  isOpen,
  selectedCount,
  onSave,
  onClose,
  isSaving,
}: BulkChangeStatusModalProps): ReactElement | null {
  const [selectedStatus, setSelectedStatus] = useState<string>(CourseStatus.ACTIVE);

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(CourseStatus.ACTIVE);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const bodyText = BULK_STATUS_MODAL_TEXT.Body.replace('[#]', String(selectedCount));

  const options = COURSE_STATUS_OPTIONS.map((o) => ({
    id: `bulk-status-${o.value}`,
    labelText: o.text,
    value: o.value,
  }));

  return (
    <NeonModalDialog id="bulk-change-status-modal" neonDialogHasClosed={onClose} dataSize="small">
      <div data-slot="dialog-header-title">{BULK_STATUS_MODAL_TEXT.Title}</div>
      <div data-slot="dialog-body">
        <p className={`__neon__text ${modalStyles.modalBodyText}`}>{bodyText}</p>
        <NeonRadioField
          id="bulk-status-radio"
          dataLabelText="Select course status"
          dataShowLabel="false"
          options={options}
          modelValue={selectedStatus}
          modelValueChange={(value: string | undefined) => value && setSelectedStatus(value)}
        />
      </div>
      <div data-slot="dialog-button-footer">
        <NeonButtonFooter
          id="bulk-change-status-footer"
          buttons={[
            {
              id: 'bulk-change-status-cancel',
              text: BULK_STATUS_MODAL_TEXT.Cancel,
              type: NeonButtonTypes.Borderless,
              onClick: (e): void => {
                (e.target as HTMLElement)?.dispatchEvent(
                  new Event('neonDialogClose', { bubbles: true })
                );
              },
            },
          ]}
          primaryButton={{
            id: 'bulk-change-status-save',
            text: BULK_STATUS_MODAL_TEXT.Confirm,
            type: NeonButtonTypes.Primary,
            disabled: isSaving,
            onClick: (): void => onSave(selectedStatus),
          }}
        />
      </div>
    </NeonModalDialog>
  );
}
